<template>
	<div class="mg-b-10 mg-lg-b-15 mg-xl-b-0">
		<div v-if="loading">
			<spinner-component :loading="loading"/>
		</div>

		<div v-if="error" class="tx-center tx-danger">
			{{ error }}
		</div>

		<div v-else class="row justify-content-center">
            <div v-if="!loading && company" class="col-lg-8 pd-x-lg-0 mb-0">
                <alert-success :form="form" :class="'shadow-sm'" message="Your changes have been saved!"></alert-success>
				<approval-alert-component :hasPendingApproval="company.hasPendingApproval"></approval-alert-component>

                <h3 class="tx-color-01 mg-b-10">Company Information</h3>
                <p class="tx-color-03 mg-b-10">Please provide us with your company details.</p>

                <form class="row" @submit.prevent="save">
                    <div class="form-group col-md-6">
						<label>Company Name</label>
						<input v-model="form.name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('name') }" placeholder="Enter company name" required>
						<has-error :form="form" field="name"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Registration Number</label>
						<input v-model="form.registration_number" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('registration_number') }" placeholder="Enter company registration number" required>
						<has-error :form="form" field="registration_number"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Email Address</label>
						<input v-model="form.email" type="email" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('email') }" placeholder="Enter your email address" required>
						<has-error :form="form" field="email"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Phone Number</label>
						<phone-number-component :country="form.country" :phone-number="form.phone" @update-phone-country="updatePhoneCountry" @update-phone="updatePhone" :readonly="false"></phone-number-component>
						<has-error :form="form" field="phone"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Street Address</label>
						<input v-model="form.address_line_1" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_1') }" placeholder="Enter your street address" required>
						<has-error :form="form" field="address_line_1"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>House Number</label>
						<input v-model="form.address_line_2" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_2') }" placeholder="Enter your house number" required>
						<has-error :form="form" field="address_line_2"></has-error>
					</div>
                    <div class="form-group col-12">
						<label>Postal Address</label>
						<input v-model="form.address_line_3" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_3') }" placeholder="Enter your postal address" required>
						<has-error :form="form" field="address_line_3"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Country</label>
						<country-picker-component :countries="options.countries" :country-selected="form.country" @update-country="updateCountry"></country-picker-component>
						<has-error :form="form" field="country"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>City/Town</label>
						<city-picker-component :cities="options.cities" :country-selected="form.country_iso" :city-selected="form.city" @update-city="updateCity"></city-picker-component>
						<has-error :form="form" field="city"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>In Which Country Are You Resident For Tax Purposes?</label>
						<select v-model="form.country_for_tax" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('country_for_tax') }">
                            <option selected :value="null" disabled>Select an option</option>
							<option v-for="option in options.countries" :value="option.name" :key="option.id">{{ option.name }}</option>
                        </select>
						<has-error :form="form" field="country_for_tax"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Industry</label>
						<select v-model="form.industry" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('industry') }" required>
							<option selected :value="null" disabled>Select an option</option>
							<option v-for="option in options.industry" :value="option.id" :key="option.id">{{ option.name }}</option>
						</select>
						<has-error :form="form" field="industry"></has-error>
					</div>
					
					<div class="col-12">
						<div class="custom-control custom-checkbox">
							<input type="checkbox" v-model="form.hasAgreed" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('hasAgreed') }" id="hasAgreed">
							<label class="custom-control-label tx-color-01" for="hasAgreed">Investment Management Agreement</label>
						</div>
						<p class="tx-color-03 mg-b-30">
							By checking the box, I hereby declare that I have read and understood the <a href="/storage/documents/IC-Asset-Managers_Retail-Investment-Management-Agreement.pdf" target="_blank">Investment Management Agreement</a> and that I
							hereby agree to it. I declare that all information provided is true, accurate and correct as at today. 
							I instruct IC Asset Managers (Ghana) LTD and IC Securities (Ghana) LTD to act based on this information, unless
							otherwise notified in writing by me. I authorise IC Asset Managers (Ghana) LTD and IC Securities (Ghana) LTD to act
							on instructions relating to my account(s) received from the Authorised Signatory(ies).
						</p>
					</div>
					
					<div class="col-12 border-top mt-3">
						<button-component :busy="form.busy" :label="'Save'" :label-loading="'Saving changes...'" :button-class="'btn btn-default float-right mt-3 shadow-sm'"></button-component>
					</div>
				</form>
            </div>
        </div>
	</div>
</template>

<style scoped>
	@media (max-width: 425px) {
		.hide {
			display: none;
		}
	}
</style>

<script>
	export default {
		data() {
			return {
				loading: false,
				error: null,
				options: null,
				company: null,
				form: new Form({
					type_id: null,
					name: null,
					registration_number: null,
					email: null,
					phone: null,
					address_line_1: null,
					address_line_2: null,
					address_line_3: null,
					country: null,
					country_iso: null,
					city: null,
					country_for_tax: null,
					industry: null,
					hasAgreed: null,
				}),
			};
		},
		created() {
			this.fetchData();
		},
		watch: {
			$route: "fetchData",
		},
		methods: {
			updateCountry: function (country) {
				this.form.country = country.code;
				this.form.country_iso = country.iso;
			},
			updateCity: function (city) {
				this.form.city = city;
			},
			updatePhoneCountry(value) {
				this.form.country = value;
			},
			updatePhone(value) {
				this.form.phone = value;
			},
			fetchCompanyOptions() {
				return axios.get("/api/options/company");
			},
			fetchCompanyInformation() {
				return axios.get(
					`/api/companies/${this.$store.getters.company.external_client_id}`
				);
			},
			fetchData() {
				this.error = null;
				this.loading = true;

				axios
					.all([
						this.fetchCompanyOptions(),
						this.fetchCompanyInformation(),
					])
					.then(
						axios.spread((options, company) => {
							this.options = options.data.data.options;
							this.company = company.data.data.company;

							if (!_.isEmpty(this.company)) {
								this.form.fill({
									type_id: this.company.id,
									name: this.company.name,
									registration_number: this.company
										.registration_number,
									email: this.company.email,
									phone: this.company.phone,
									address_line_1: this.company.address_line_1,
									address_line_2: this.company.address_line_2,
									address_line_3: this.company.address_line_3,
									country: this.company.country,
									country_iso: this.company.country_details
										.code_iso,
									city: this.company.city,
									country_for_tax: this.company.country_for_tax,
									industry: this.company.industry,
									hasAgreed: this.company.hasAgreed,
								});
							} else {
								this.company = [];
							}
						})
					)
					.catch((error) => {
						this.error = error.toString();
					})
					.finally(() => {
						this.loading = false;
					});
			},
			save() {
				this.form
					.patch(
						`/api/companies/${this.$store.getters.company.external_client_id}`,
						{
							params: {
								type: "company_profile_information",
							},
						}
					)
					.finally(() => {
						window.scrollTo(0, 0);
					});
			},
		},
	};
</script>
